import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Eye, TrendingUp, Users, Globe, Clock, Building2, 
  ChevronRight, BarChart3, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import api from '../utils/api';
import { toFullUrl } from '../utils/imageUrl';
import { getThumbnailUrl } from '../utils/cloudinaryUpload';

const ProfileAnalytics = ({ compact = false }) => {
  const [analytics, setAnalytics] = useState(null);
  const [viewers, setViewers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllViewers, setShowAllViewers] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const [analyticsRes, viewersRes] = await Promise.all([
        api.get('/companies/me/profile-analytics').catch(() => ({ data: null })),
        api.get('/companies/me/profile-views?days=30').catch(() => ({ data: null }))
      ]);
      
      setAnalytics(analyticsRes.data);
      setViewers(viewersRes.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-slate-200 rounded w-1/3"></div>
            <div className="h-8 bg-slate-200 rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!analytics || analytics.error) {
    return null;
  }

  // Calculate trend
  const weeklyTrend = analytics.views_week > 0 
    ? Math.round(((analytics.views_week - (analytics.views_month - analytics.views_week) / 3) / Math.max(1, (analytics.views_month - analytics.views_week) / 3)) * 100)
    : 0;
  const trendUp = weeklyTrend >= 0;

  if (compact) {
    return (
      <Card className="hover:shadow-lg transition-shadow">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-slate-600 mb-1">Profile Views</p>
              <p className="text-2xl sm:text-3xl font-bold text-purple-500">{analytics.views_month || 0}</p>
              <div className="flex items-center gap-1 mt-1">
                {trendUp ? (
                  <ArrowUpRight className="h-3 w-3 text-green-500" />
                ) : (
                  <ArrowDownRight className="h-3 w-3 text-red-500" />
                )}
                <span className={`text-xs ${trendUp ? 'text-green-600' : 'text-red-600'}`}>
                  {Math.abs(weeklyTrend)}% this week
                </span>
              </div>
            </div>
            <Eye className="h-8 w-8 sm:h-10 sm:w-10 text-purple-200" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Eye className="h-5 w-5 text-purple-500" />
          Who Viewed Your Profile
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <p className="text-2xl font-bold text-purple-600">{analytics.views_today || 0}</p>
            <p className="text-xs text-slate-600">Today</p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{analytics.views_week || 0}</p>
            <p className="text-xs text-slate-600">This Week</p>
          </div>
          <div className="text-center p-3 bg-cyan-50 rounded-lg">
            <p className="text-2xl font-bold text-cyan-600">{analytics.views_month || 0}</p>
            <p className="text-xs text-slate-600">This Month</p>
          </div>
        </div>

        {/* Mini Chart - 7 day trend */}
        {analytics.daily_views && analytics.daily_views.length > 0 && (
          <div className="pt-2">
            <p className="text-xs text-slate-500 mb-2">Last 7 Days</p>
            <div className="flex items-end gap-1 h-12">
              {analytics.daily_views.map((day, i) => {
                const maxViews = Math.max(...analytics.daily_views.map(d => d.views), 1);
                const height = (day.views / maxViews) * 100;
                return (
                  <div 
                    key={i} 
                    className="flex-1 bg-purple-200 rounded-t hover:bg-purple-300 transition-colors"
                    style={{ height: `${Math.max(height, 8)}%` }}
                    title={`${day.date}: ${day.views} views`}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Recent Viewers */}
        {viewers && viewers.summary && viewers.summary.length > 0 && (
          <div className="pt-2 border-t">
            <p className="text-sm font-medium mb-3">Recent Viewers</p>
            <div className="space-y-2">
              {viewers.summary.slice(0, showAllViewers ? 10 : 3).map((viewer, i) => (
                <div 
                  key={i} 
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => window.open(`/company/${viewer.company_id}`, '_blank')}
                  data-testid={`viewer-${i}`}
                >
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center text-white font-bold text-sm overflow-hidden flex-shrink-0">
                    {viewer.company_logo ? (
                      <img 
                        src={getThumbnailUrl(toFullUrl(viewer.company_logo), 80)} 
                        alt={viewer.company_name} 
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      viewer.company_name?.charAt(0) || '?'
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{viewer.company_name || 'Unknown Company'}</p>
                    <p className="text-xs text-slate-500">{viewer.country || 'Caribbean'}</p>
                  </div>
                  <div className="text-right">
                    <Badge variant="secondary" className="text-xs">
                      {viewer.view_count} {viewer.view_count === 1 ? 'view' : 'views'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
            
            {viewers.summary.length > 3 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full mt-2 text-purple-600"
                onClick={() => setShowAllViewers(!showAllViewers)}
              >
                {showAllViewers ? 'Show Less' : `Show ${viewers.summary.length - 3} More`}
                <ChevronRight className={`h-4 w-4 ml-1 transition-transform ${showAllViewers ? 'rotate-90' : ''}`} />
              </Button>
            )}
          </div>
        )}

        {/* Top Countries */}
        {analytics.top_countries && analytics.top_countries.length > 0 && (
          <div className="pt-2 border-t">
            <p className="text-sm font-medium mb-2 flex items-center gap-2">
              <Globe className="h-4 w-4 text-slate-400" />
              Top Viewer Countries
            </p>
            <div className="flex flex-wrap gap-2">
              {analytics.top_countries.map((country, i) => (
                <Badge key={i} variant="outline" className="text-xs">
                  {country.country}: {country.views}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {(!viewers?.summary || viewers.summary.length === 0) && (
          <div className="text-center py-6 text-slate-500">
            <Eye className="h-10 w-10 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">No profile views yet</p>
            <p className="text-xs mt-1">Complete your profile to attract more viewers</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProfileAnalytics;
